import React, { useEffect, useState, useMemo } from "react";
import { api, formatIDR } from "@/lib/api";
import { useLang } from "@/contexts/LangContext";
import { useConfig } from "@/contexts/ConfigContext";
import { Card } from "@/components/ui/card";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell } from "recharts";

const CHART_MARGIN = { top: 10, right: 10, bottom: 0, left: 10 };
const TOOLTIP_STYLE = { borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12 };
const AXIS_TICK = { fontSize: 11, fill: "#64748B" };
const yTickFormatter = (v) => {
  if (v >= 1e9) return (v / 1e9).toFixed(1) + "M";
  if (v >= 1e6) return (v / 1e6).toFixed(0) + "jt";
  return v;
};
const BAR_RADIUS = [6, 6, 0, 0];

const Reports = () => {
  const { t } = useLang();
  const cfg = useConfig();
  const [stats, setStats] = useState(null);
  const [deals, setDeals] = useState([]);

  useEffect(() => {
    (async () => {
      const [s, d] = await Promise.all([api.get("/stats/overview"), api.get("/deals")]);
      setStats(s.data);
      setDeals(d.data);
    })();
  }, []);

  const revenueData = useMemo(
    () => stats ? (cfg?.stagesByWeight || []).map((s) => ({
      stage: cfg.stageLabel(s.key), key: s.key,
      value: stats.stage_value[s.key] || 0, color: cfg.stageStyle(s.key).hex,
    })) : [],
    [stats, cfg]
  );

  if (!stats) return <div className="text-slate-500">Loading...</div>;

  return (
    <div className="space-y-6" data-testid="reports-view">
      <div>
        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>{t("reports")}</h2>
        <p className="text-sm text-slate-500 mt-1">{deals.length} {t("deals").toLowerCase()}</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="p-5 border-slate-200 shadow-sm">
          <h3 className="text-base font-semibold text-slate-900 mb-4">{t("revenue_by_stage")}</h3>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={revenueData} margin={CHART_MARGIN}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                <XAxis dataKey="stage" tick={AXIS_TICK} interval={0} angle={-18} textAnchor="end" height={56} />
                <YAxis tick={AXIS_TICK} tickFormatter={yTickFormatter} />
                <Tooltip formatter={(v) => formatIDR(v)} contentStyle={TOOLTIP_STYLE} />
                <Bar dataKey="value" radius={BAR_RADIUS}>
                  {revenueData.map((d) => <Cell key={d.key} fill={d.color} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-5 border-slate-200 shadow-sm">
          <h3 className="text-base font-semibold text-slate-900 mb-4">Summary</h3>
          <ul className="divide-y divide-slate-100">
            <li className="flex justify-between py-3"><span className="text-sm text-slate-600">{t("total_pipeline")}</span><span className="font-semibold text-slate-900">{formatIDR(stats.total_pipeline)}</span></li>
            <li className="flex justify-between py-3"><span className="text-sm text-slate-600">{t("weighted_forecast")}</span><span className="font-semibold text-slate-900">{formatIDR(stats.weighted_forecast)}</span></li>
            <li className="flex justify-between py-3"><span className="text-sm text-slate-600">{t("win_rate")}</span><span className="font-semibold text-slate-900">{stats.win_rate}%</span></li>
            <li className="flex justify-between py-3"><span className="text-sm text-slate-600">{t("won_count")}</span><span className="font-semibold text-emerald-700">{stats.won_count}</span></li>
            <li className="flex justify-between py-3"><span className="text-sm text-slate-600">{t("lost_count")}</span><span className="font-semibold text-rose-700">{stats.lost_count}</span></li>
            <li className="flex justify-between py-3"><span className="text-sm text-slate-600">{t("active_deals")}</span><span className="font-semibold text-slate-900">{stats.active_deals}</span></li>
            <li className="flex justify-between py-3"><span className="text-sm text-slate-600">Avg Deal</span><span className="font-semibold text-slate-900">{formatIDR(stats.avg_deal)}</span></li>
          </ul>
        </Card>
      </div>
    </div>
  );
};

export default Reports;
